import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  DriverStateRecord,
  getDriverHistory,
  listDriverStates,
} from "../../api";
import { Icon } from "../../icons";

export function DriverPage() {
  const { t, i18n } = useTranslation(["dashboard", "common"]);
  const [states, setStates] = useState<DriverStateRecord[]>([]);
  const [riskyOnly, setRiskyOnly] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);
  const [history, setHistory] = useState<DriverStateRecord[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  async function refresh() {
    setError(null);
    try {
      const rows = await listDriverStates({ risky_only: riskyOnly });
      setStates(rows);
      if (!selected && rows.length > 0) setSelected(rows[0].vehicle_id);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
    const timer = setInterval(refresh, 15_000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [riskyOnly]);

  useEffect(() => {
    if (!selected) return;
    getDriverHistory(selected, 30)
      .then(setHistory)
      .catch((e) => setError(e instanceof Error ? e.message : String(e)));
  }, [selected]);

  const trendMaxPerclos = history.length > 0 ? Math.max(...history.map((h) => h.perclos_pct), 1) : 1;

  return (
    <div className="page">
      <div className="page-head-row">
        <div>
          <h2 style={{ marginTop: 0 }}>
            <Icon name="eye" /> {t("driver.title")}
          </h2>
          <p className="muted">{t("driver.intro")}</p>
        </div>
        <label className="row-form">
          <input
            type="checkbox"
            checked={riskyOnly}
            onChange={(e) => setRiskyOnly(e.target.checked)}
          />{" "}
          {t("driver.riskyOnly")}
        </label>
      </div>
      {error && <p className="error">{error}</p>}

      {loading ? (
        <p className="muted">{t("common:actions.loading")}</p>
      ) : states.length === 0 ? (
        <div className="empty-card">
          <Icon name="eye" /> {t("driver.emptyStates")}
        </div>
      ) : (
        <div className="delivery-layout">
          <div className="delivery-list-col">
            <h3>{t("driver.fleetStatus")}</h3>
            <ul className="delivery-list">
              {states.map((s) => (
                <li
                  key={s.vehicle_id}
                  className={s.vehicle_id === selected ? "active" : ""}
                  onClick={() => setSelected(s.vehicle_id)}
                >
                  <div className="delivery-list-head">
                    <strong>{s.vehicle_id}</strong>
                    {s.alert_severity && (
                      <span className={`severity-pill sev-${s.alert_severity}`}>
                        {t(`driver.severity.${s.alert_severity}`, { defaultValue: s.alert_severity })}
                      </span>
                    )}
                  </div>
                  <div className="delivery-list-meta">
                    {t("driver.listMeta", {
                      perclos: s.perclos_pct.toFixed(0),
                      risk: (s.microsleep_risk * 100).toFixed(0),
                      yawns: s.yawns_15min,
                    })}
                  </div>
                </li>
              ))}
            </ul>
          </div>

          <div className="delivery-detail-col">
            {(() => {
              const s = states.find((x) => x.vehicle_id === selected);
              if (!s) return <p className="muted">{t("driver.selectPrompt")}</p>;
              return (
                <div>
                  <h3>{t("driver.vehicleTitle", { id: s.vehicle_id })}</h3>
                  <p className="muted small">
                    {t("driver.driverLine", {
                      driver: s.driver_id ?? "—",
                      date: new Date(s.updated_at).toLocaleString(i18n.resolvedLanguage),
                    })}
                  </p>
                  <div className="kpi-grid">
                    <KpiBox label={t("driver.kpi.perclos")} value={`%${s.perclos_pct.toFixed(0)}`} tone={s.perclos_pct >= 30 ? "rose" : s.perclos_pct >= 20 ? "amber" : "emerald"} />
                    <KpiBox label={t("driver.kpi.microsleep")} value={`${(s.microsleep_risk * 100).toFixed(0)}%`} tone={s.microsleep_risk >= 0.7 ? "rose" : s.microsleep_risk >= 0.4 ? "amber" : "emerald"} />
                    <KpiBox label={t("driver.kpi.yawns")} value={String(s.yawns_15min)} tone={s.yawns_15min >= 6 ? "rose" : s.yawns_15min >= 3 ? "amber" : "emerald"} />
                    <KpiBox label={t("driver.kpi.headDrops")} value={String(s.head_drops_15min)} tone={s.head_drops_15min >= 4 ? "rose" : "indigo"} />
                    {s.stress_score != null && (
                      <KpiBox label={t("driver.kpi.stress")} value={(s.stress_score * 100).toFixed(0) + "%"} tone="violet" />
                    )}
                    {s.aggressive_score != null && (
                      <KpiBox label={t("driver.kpi.aggressive")} value={(s.aggressive_score * 100).toFixed(0) + "%"} tone="amber" />
                    )}
                  </div>

                  {history.length > 0 && (
                    <>
                      <h4 style={{ marginTop: "1rem" }}>
                        {t("driver.trendTitle", { count: history.length })}
                      </h4>
                      <svg viewBox="0 0 200 50" preserveAspectRatio="none" className="trend-svg" style={{ width: "100%", height: 60 }}>
                        <polyline
                          fill="none"
                          stroke="#f43f5e"
                          strokeWidth="1.6"
                          points={history
                            .slice()
                            .reverse()
                            .map((h, i) =>
                              `${(i / Math.max(1, history.length - 1)) * 200},${50 - (h.perclos_pct / trendMaxPerclos) * 45}`,
                            )
                            .join(" ")}
                        />
                      </svg>
                    </>
                  )}
                </div>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
}

function KpiBox({ label, value, tone }: { label: string; value: string; tone: string }) {
  return (
    <div className={`kpi-card tone-${tone}`}>
      <div className="kpi-icon">
        <Icon name="eye" size={20} />
      </div>
      <div className="kpi-body">
        <div className="kpi-label">{label}</div>
        <div className="kpi-value">{value}</div>
      </div>
    </div>
  );
}
